import { Component, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatError, MatFormField, MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { Router } from '@angular/router';
import { NgxPaginationModule } from 'ngx-pagination';
import { NgxSpinnerService } from 'ngx-spinner';
import { CommonPaginationComponent } from '../common-component-pagination/common-component-pagination.component';
import { CommonApiService } from '../CommonController/common-api-service';
import { SensoriseSimDataApiService } from '../CommonController/sensorise-sim-data-details-api-service';
import { exportAsCsvFile } from '../shared/function/exportDataToCsv';
import { ViewChild, ElementRef } from '@angular/core';

@Component({
  selector: 'app-production-device',
  templateUrl: './sensorise-sim-data-details.component.html',
  styleUrls: ['./sensorise-sim-data-details.component.scss'],
  standalone: true,
  imports: [
    MatIcon,
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    MatError,
    CommonPaginationComponent,
    NgxPaginationModule,
  ],
})
export class SensoriseSimDataComponent implements OnInit {
  sensoriseSimDataDetailsForm!: FormGroup;

  fileToUpload: File | null = null; // Store the file outside the form
  fileUploaded = false;

  @ViewChild('fileInput') fileInput!: ElementRef;

  userPermission = [];
  userType = '';
  loggedId = '';

  isUploadBoxVisible = true;

  exportData = [];

  presentSensoriseSimDataDetails = [];
  productionUploadedDevicePage = 1;
  productionUploadedDeviceSize = 10;

  excelDuplicateIccidDetails = [];
  productionDuplicateDevicePage = 1;
  productionDuplicateDeviceSize = 10;

  iccidNotPresentInSensoriseDatabase = [];
  productionInvalidDevicePage = 1;
  productionInvalidDeviceSize = 10;

  constructor(
    public sensoriseSimDataApiService: SensoriseSimDataApiService,
    public commonApiService: CommonApiService,
    private fb: FormBuilder,
    public router: Router,
    private spinner: NgxSpinnerService
  ) {}

  ngOnInit(): void {
    this.checkPermission();
    this.isUploadBoxVisible = true;
    this.setSensoriseSimDataForm();
    this.onFileChange('');
  }

  checkPermission() {
    this.userPermission = JSON.parse(sessionStorage.getItem('userPermissions'));
    this.loggedId = sessionStorage.getItem('loggedInUserId');
    this.userPermission.forEach((element) => {
      if (
        element.pageName === 'Sensorise SIM Data Details' &&
        element.view === false
      ) {
        this.navigateBack();
      }
    });
  }
  resetPage(): void {
    // reset form
    this.sensoriseSimDataDetailsForm.reset();
    this.setSensoriseSimDataForm();

    // reset file state
    this.fileToUpload = null;
    this.fileUploaded = false;

    // Clear browser file cache
    if (this.fileInput) {
      this.fileInput.nativeElement.value = '';
    }
  }
  setSensoriseSimDataForm() {
    this.sensoriseSimDataDetailsForm = this.fb.group({
      productionDetails: new FormControl('', [Validators.required]),
      file: new FormControl('', [Validators.required]),
    });
  }

  onFileChange(ev) {
    try {
      if (ev.target.files && ev.target.files.length > 0) {
        const file = ev.target.files[0];

        if (file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {
          // Store file in variable (not the form)
          this.sensoriseSimDataDetailsForm.get('file')!.setValue(file.name);
          this.fileToUpload = file;
          this.fileUploaded = true;
        } else {
          this.commonApiService.openSnackbar(
            'Please upload a valid Excel file'
          );
        }
      } else {
        this.fileToUpload = null;
        this.fileUploaded = false;
        this.sensoriseSimDataDetailsForm.get('file').setValue('');
      }
    } catch (error) {
      this.commonApiService.openSnackbar(
        error.error.message || 'Please upload a valid Excel file'
      );
      this.sensoriseSimDataDetailsForm.get('file').setValue('');
      this.fileToUpload = null;
      this.fileUploaded = false;
    }
  }

  saveDeviceDetails() {
    // Clear existing lists before starting a new upload
    this.presentSensoriseSimDataDetails = [];
    this.excelDuplicateIccidDetails = [];
    this.iccidNotPresentInSensoriseDatabase = [];

    // Create FormData for file upload
    const formData = new FormData();
    if (this.fileToUpload) {
      formData.append('file', this.fileToUpload); // Attach the file
    } else {
      this.commonApiService.openSnackbar(
        'Please upload file before submitting.'
      );
    }

    // Retrieve userId from session storage
    const userId = this.loggedId;
    if (userId) {
      formData.append('userId', userId); // Append userId dynamically
    } else {
      this.commonApiService.openSnackbar(
        'User ID not found. Please log in again.'
      );
      return; // Stop execution if userId is missing
    }

    // Attach form values (if any) to the FormData
    const payload = this.sensoriseSimDataDetailsForm.value;
    for (const key in payload) {
      if (payload[key] && key !== 'file') {
        formData.append(key, payload[key]);
      }
    }

    this.spinner.show();

    this.sensoriseSimDataApiService
      .getSensoriseSimDataUsingExcel(formData)
      .subscribe({
        next: (res) => {
          this.spinner.hide();

          if (res.success) {
            // Check success using 'success' property
            this.isUploadBoxVisible = false;
            this.sensoriseSimDataDetailsForm.reset();
            this.commonApiService.openSnackbar(res.message);

            // Map the successfully uploaded devices from `data`
            if (
              res.data.simDetails.simDetailEntity &&
              res.data.simDetails.simDetailEntity.length > 0
            ) {
              this.presentSensoriseSimDataDetails =
                res.data.simDetails.simDetailEntity;
            }

            // Map the error messages for invalid devices
            if (res.data.duplicateRows && res.data.duplicateRows.length > 0) {
              this.excelDuplicateIccidDetails = res.data.duplicateRows;
            }

            // Map the error messages for invalid devices
            if (res.data.errors && res.data.errors.length > 0) {
              this.iccidNotPresentInSensoriseDatabase = res.data.errors;
            }

            this.commonApiService.openSnackbar(res.message);
          } else {
            this.commonApiService.openSnackbar(
              'Failed to upload production data'
            );
          }
        },
        error: (error) => {
          this.spinner.hide();
          this.commonApiService.openSnackbar(
            error.error.message ||
              'An error occurred while uploading production data'
          );
        },
      });
  }

  onPageChangeSensoriseDataNotPresentList(event) {
    this.productionUploadedDevicePage = event;
  }

  onSizeChangeSensoriseDataNotPresentList(newPageSize) {
    this.productionUploadedDeviceSize = newPageSize;
    this.productionUploadedDevicePage = 1;
  }

  onPageChangeExcelDuplicateIccidList(event) {
    this.productionDuplicateDevicePage = event;
  }

  onSizeChangeExcelDuplicateIccidList(newPageSize) {
    this.productionDuplicateDeviceSize = newPageSize;
    this.productionDuplicateDevicePage = 1;
  }

  onPageChangeSensoriseDataPresentList(event) {
    this.productionInvalidDevicePage = event;
  }

  onSizeChangeSensoriseDataPresentList(newPageSize) {
    this.productionInvalidDeviceSize = newPageSize;
    this.productionInvalidDevicePage = 1;
  }

  downloadMyFile() {
    const link = document.createElement('a');
    link.setAttribute('target', '_blank');
    link.setAttribute('href', '../../assets/Sensorise_SIM_data_Details.xlsx');
    link.setAttribute('download', `Sensorise_SIM_data_Details.xlsx`);
    document.body.appendChild(link);
    link.click();
    link.remove();
  }

  exportToExcelUploadedProducedDeviceData(presentSensoriseSimDataDetails) {
    if (confirm('Are you sure?')) {
      this.exportData = presentSensoriseSimDataDetails;
      exportAsCsvFile(this.exportData, 'Uploaded_Produced_Device_Details_');
      this.commonApiService.openSnackbar('File Ready For Download.');
    }
  }
  exportToExcelInvalidProducedDeviceData(iccidNotPresentInSensoriseDatabase) {
    if (confirm('Are you sure?')) {
      this.exportData = iccidNotPresentInSensoriseDatabase;
      exportAsCsvFile(this.exportData, 'Invalid_Produced_Device_Details_');
      this.commonApiService.openSnackbar('File Ready For Download.');
    }
  }

  exportToExcelDuplicateProducedDeviceData(excelDuplicateIccidDetails) {
    if (confirm('Are you sure?')) {
      this.exportData = excelDuplicateIccidDetails;
      exportAsCsvFile(this.exportData, 'Duplicate_Produced_Device_Details_');
      this.commonApiService.openSnackbar('File Ready For Download.');
    }
  }

  openManualIccidPage() {
    this.router.navigate(['/sensorise-sim-manual-upload']);
  }

  redirect(to): void {
    this.router.navigate([to]);
  }

  navigateBack() {
    this.commonApiService.back();
  }
}
